import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Fade,
  Skeleton,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh,
  AttachMoney,
  ShoppingCart,
  Receipt,
  Warning,
  CalendarToday,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CollectorDashboard from './CollectorDashboard';

interface WeeklyProgress {
  collected_today: number;
  collected_this_week: number;
  pending_this_week: number;
  total_expected_this_week: number;
  weekly_progress_pct: number;
  collected_prev_week: number;
  week_start: string;
  week_end: string;
}

interface PersonBreakdown {
  name: string;
  total: number;
  cash: number;
  transfer: number;
  count: number;
}

interface DelinquentItem {
  customer_name: string;
  sale_id: number;
  product_name: string;
  days_overdue: number;
  overdue_amount: number;
  vendor_name?: string;
}

interface UpcomingDay {
  date: string;
  day_name: string;
  pending_count: number;
  pending_amount: number;
}

interface DashboardData {
  date: string;
  date_label: string;
  vendors: PersonBreakdown[];
  collectors: PersonBreakdown[];
  new_sales_count: number;
  new_sales_amount: number;
  expenses_count: number;
  expenses_amount: number;
  delinquent: DelinquentItem[];
  upcoming: UpcomingDay[];
}

interface WeeklyCollectorDay {
  date: string;
  day_name: string;
  total: number;
  cash: number;
  transfer: number;
  count: number;
}

interface WeeklyCollectorBreakdown {
  name: string;
  days: WeeklyCollectorDay[];
  week_total: number;
  week_cash: number;
  week_transfer: number;
  week_count: number;
}

interface WeeklyByCollectorData {
  week_start: string;
  week_end: string;
  collectors: WeeklyCollectorBreakdown[];
}

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const cardSx = (theme: any) => ({
  height: '100%',
  background: theme.palette.mode === 'dark' ? 'rgba(22, 27, 46, 0.6)' : '#ffffff',
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
});

function PersonCard({ person, color, onClick }: { person: PersonBreakdown; color: string; onClick?: () => void }) {
  return (
    <Card
      sx={(theme) => ({
        ...cardSx(theme),
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        ...(onClick ? { '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${color}30` } } : {}),
      })}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.85rem' }, fontWeight: 700, color: 'text.primary' }} noWrap>
            {person.name}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.6rem' }, fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
          {fmt(person.total)}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' }, color: 'text.secondary' }}>
          Ef: {fmt(person.cash)} / Tr: {fmt(person.transfer)}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' }, color: 'text.secondary', mt: 0.25 }}>
          {person.count} cobro{person.count !== 1 ? 's' : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState<DashboardData | null>(null);
  const [weeklyProgress, setWeeklyProgress] = useState<WeeklyProgress | null>(null);
  const [weeklyByCollector, setWeeklyByCollector] = useState<WeeklyByCollectorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(toDateStr(new Date()));

  const isToday = selectedDate === toDateStr(new Date());

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate]);

  const fetchData = async (date: string) => {
    setLoading(true);
    try {
      const [summaryRes, progressRes, weeklyByCollectorRes] = await Promise.all([
        api.get(`/dashboard/summary?date=${date}`),
        api.get(`/dashboard/weekly-progress?date=${date}`),
        api.get(`/dashboard/weekly-by-collector?date=${date}`),
      ]);
      setData(summaryRes.data);
      setWeeklyProgress(progressRes.data);
      setWeeklyByCollector(weeklyByCollectorRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (days: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(toDateStr(d));
  };

  if (user?.role === 'cobrador') {
    return <CollectorDashboard />;
  }

  if (loading || !data) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
        <Skeleton variant="text" width="300px" height={50} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 3 }} />
      </Container>
    );
  }

  const personColors = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#3B82F6'];

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 }, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontSize: { xs: '1.3rem', sm: '1.7rem' }, fontWeight: 800, color: 'text.primary' }}
          >
            Resumen del Dia
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, textTransform: 'capitalize' }}>
            {data.date_label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={() => changeDate(-1)} size="small">
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{ width: { xs: 130, sm: 150 }, '& input': { fontSize: '0.8rem', py: 0.75 } }}
          />
          <IconButton onClick={() => changeDate(1)} size="small" disabled={isToday}>
            <ArrowForward sx={{ fontSize: 20 }} />
          </IconButton>
          <Tooltip title="Actualizar">
            <IconButton
              onClick={() => fetchData(selectedDate)}
              sx={{
                width: 36, height: 36,
                backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
              }}
            >
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Progreso semanal */}
      {weeklyProgress && (
        <Fade in timeout={200}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
              Progreso semanal
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 1.5 }}>
              <Grid item xs={6} sm={3} md={3}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Cobrado hoy</Typography>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: '#10B981' }}>{fmt(weeklyProgress.collected_today)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Cobrado semana</Typography>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: '#6366F1' }}>{fmt(weeklyProgress.collected_this_week)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Pendiente semana</Typography>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: '#F59E0B' }}>{fmt(weeklyProgress.pending_this_week)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <Card sx={cardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>Avance Semanal</Typography>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: '#6366F1' }}>{weeklyProgress.weekly_progress_pct}%</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(weeklyProgress.weekly_progress_pct, 100)}
                      sx={{ mt: 0.5, height: 6, borderRadius: 3, backgroundColor: 'rgba(99,102,241,0.1)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', borderRadius: 3 } }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Cobros por cobrador */}
      {data.collectors.length > 0 && (
        <Fade in timeout={300}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
              Cobros por cobrador (día seleccionado)
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {data.collectors.map((c, i) => (
                <Grid item xs={6} sm={6} md={4} key={c.name}>
                  <Fade in timeout={300 + i * 100}>
                    <Box>
                      <PersonCard
                        person={c}
                        color={personColors[(i + 2) % personColors.length]}
                        onClick={() => navigate(`/collections?collector=${encodeURIComponent(c.name)}`)}
                      />
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>
      )}

      {/* Cobros semanales por cobrador */}
      {weeklyByCollector && weeklyByCollector.collectors.length > 0 && (
        <Fade in timeout={350}>
          <Card sx={(theme) => ({ ...cardSx(theme), mb: { xs: 2, sm: 3 } })}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                Cobros semanales por cobrador (Mié–Mar)
              </Typography>
              {isMobile ? (
                // Mobile: una card por cobrador con sus 7 días apilados
                <Stack spacing={1.25}>
                  {weeklyByCollector.collectors.map((c) => (
                    <Box
                      key={c.name}
                      onClick={() => navigate(`/collections?collector=${encodeURIComponent(c.name)}`)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:active': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', textTransform: 'capitalize' }}>
                          {c.name}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#10B981' }}>
                            {fmt(c.week_total)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {c.week_count} cobro{c.week_count !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {c.days.map((d) => (
                          <Box
                            key={d.date}
                            sx={{
                              textAlign: 'center',
                              p: 0.5,
                              borderRadius: 0.75,
                              backgroundColor: d.total > 0 ? 'rgba(16,185,129,0.08)' : 'transparent',
                            }}
                          >
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 600 }}>
                              {d.day_name.slice(0, 3)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: d.total > 0 ? 700 : 400, color: d.total > 0 ? 'text.primary' : 'text.disabled' }}>
                              {d.total > 0 ? (d.total >= 1000 ? `${Math.round(d.total/1000)}k` : d.total) : '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                  {/* Totales semanales */}
                  <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(16,185,129,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>TOTAL SEMANA</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#10B981' }}>
                      {fmt(weeklyByCollector.collectors.reduce((sum, c) => sum + c.week_total, 0))}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box component="th" sx={{ p: 1, textAlign: 'left', color: 'text.secondary', fontWeight: 600 }}>Cobrador</Box>
                        {weeklyByCollector.collectors[0].days.map((d) => (
                          <Box component="th" key={d.date} sx={{ p: 1, textAlign: 'right', color: 'text.secondary', fontWeight: 600 }}>
                            {d.day_name}
                          </Box>
                        ))}
                        <Box component="th" sx={{ p: 1, textAlign: 'right', color: 'text.primary', fontWeight: 800 }}>Total semana</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {weeklyByCollector.collectors.map((c) => (
                        <Box
                          component="tr"
                          key={c.name}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
                          }}
                          onClick={() => navigate(`/collections?collector=${encodeURIComponent(c.name)}`)}
                        >
                          <Box component="td" sx={{ p: 1, fontWeight: 600, textTransform: 'capitalize' }}>{c.name}</Box>
                          {c.days.map((d) => (
                            <Box component="td" key={d.date} sx={{ p: 1, textAlign: 'right', color: d.total > 0 ? 'text.primary' : 'text.disabled' }}>
                              {d.total > 0 ? fmt(d.total) : '—'}
                            </Box>
                          ))}
                          <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: 800, color: '#10B981' }}>
                            {fmt(c.week_total)}
                            <Typography component="span" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                              {c.week_count} cobro{c.week_count !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      <Box component="tr" sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                        <Box component="td" sx={{ p: 1, fontWeight: 800 }}>TOTAL</Box>
                        {weeklyByCollector.collectors[0].days.map((_, idx) => {
                          const dayTotal = weeklyByCollector.collectors.reduce((sum, c) => sum + (c.days[idx]?.total || 0), 0);
                          return (
                            <Box component="td" key={idx} sx={{ p: 1, textAlign: 'right', fontWeight: 700 }}>
                              {dayTotal > 0 ? fmt(dayTotal) : '—'}
                            </Box>
                          );
                        })}
                        <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: 900, color: '#10B981' }}>
                          {fmt(weeklyByCollector.collectors.reduce((sum, c) => sum + c.week_total, 0))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Ventas nuevas + Gastos */}
      <Fade in timeout={400}>
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={6} sm={6}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: { xs: '10px', sm: '12px' }, flexShrink: 0,
                    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.3)', color: '#fff',
                  }}
                >
                  <ShoppingCart sx={{ fontSize: { xs: 18, sm: 22 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }} noWrap>
                    Ventas hoy
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: 'text.primary' }}>
                    {data.new_sales_count}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.8rem' }, color: 'text.secondary' }} noWrap>
                    {fmt(data.new_sales_amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: { xs: '10px', sm: '12px' }, flexShrink: 0,
                    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(239,68,68,0.3)', color: '#fff',
                  }}
                >
                  <Receipt sx={{ fontSize: { xs: 18, sm: 22 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }} noWrap>
                    Gastos hoy
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.3rem' }, fontWeight: 800, color: 'text.primary' }}>
                    {data.expenses_count}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.8rem' }, color: 'text.secondary' }} noWrap>
                    {fmt(data.expenses_amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Morosos */}
      <Fade in timeout={500}>
        <Card sx={(theme) => ({ ...cardSx(theme), mb: { xs: 2, sm: 3 } })}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Warning sx={{ color: '#EF4444', fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary' }}>
                Morosos
              </Typography>
              <Chip
                label={data.delinquent.length}
                size="small"
                sx={{
                  ml: 'auto',
                  backgroundColor: data.delinquent.length > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                  color: data.delinquent.length > 0 ? '#EF4444' : '#10B981',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
            {data.delinquent.length === 0 ? (
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', py: 1 }}>
                Sin morosos actualmente
              </Typography>
            ) : (
              <List disablePadding>
                {data.delinquent.slice(0, 10).map((d, i) => (
                  <React.Fragment key={`${d.sale_id}-${i}`}>
                    {i > 0 && <Divider />}
                    <ListItem disablePadding sx={{ py: 0.75 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              {d.customer_name}
                            </Typography>
                            <Chip
                              label={`${d.days_overdue}d atraso`}
                              size="small"
                              sx={{
                                backgroundColor: d.days_overdue > 14 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                                color: d.days_overdue > 14 ? '#EF4444' : '#D97706',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Venta #{d.sale_id} - {d.product_name} - Debe: {fmt(d.overdue_amount)}
                            {d.vendor_name ? ` (${d.vendor_name})` : ''}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Proximos cobros */}
      <Fade in timeout={600}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CalendarToday sx={{ color: '#6366F1', fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary' }}>
                Proximos cobros
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                overflowX: { xs: 'auto', sm: 'visible' },
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                pb: { xs: 1, sm: 0 },
                mx: { xs: -0.5, sm: 0 },
                px: { xs: 0.5, sm: 0 },
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {data.upcoming.map((day) => (
                <Box
                  key={day.date}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                    minWidth: { xs: '120px', sm: 'auto' },
                    flex: { xs: '0 0 auto', sm: '1 1 0' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                    {day.day_name}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: 'primary.main' }}>
                    {day.pending_count}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {fmt(day.pending_amount)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Container>
  );
}
